import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useDebounce } from '@uidotdev/usehooks';
import { Clock, FileEdit, Loader2, Search } from 'lucide-react';
import { listForReview } from '@generated/server/decisions';
import { useSession } from '@generated/components/session';
import { ReviewDecisionModal } from '@generated/components/ReviewDecisionModal';
import { LIB_LABEL, completeness, fmtDate, type Decision } from '@generated/lib/decision-utils';

export const Route = createFileRoute('/_app/review')({ component: ReviewPage });

type Lib = 'all' | 'itp' | 'sd';

function ReviewPage() {
  const { sid, user, isStaff } = useSession();
  const [lib, setLib] = useState<Lib>('all');
  const [search, setSearch] = useState('');
  const term = useDebounce(search, 300);
  const [items, setItems] = useState<Decision[]>([]);
  const [cursors, setCursors] = useState<{ itp: string | null; sd: string | null } | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [pending, setPending] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState<Decision | null>(null);
  const first = useRef(true);

  useEffect(() => {
    let active = true;
    if (first.current) setLoading(true); else setRefetching(true);
    listForReview({ data: { sid, lib, search: term || undefined } })
      .then((r) => { if (!active) return; setItems(r?.items ?? []); setCursors(r?.cursors ?? null); setHasMore(!!r?.hasMore); setPending(r?.pending ?? 0); setError(''); })
      .catch((e: Error) => { console.error(e); if (active) setError(e.message || 'Could not load decisions.'); })
      .finally(() => { if (active) { setLoading(false); setRefetching(false); first.current = false; } });
    return () => { active = false; };
  }, [sid, lib, term]);

  async function more() {
    if (!cursors) return;
    setLoadingMore(true);
    try {
      const r = await listForReview({ data: { sid, lib, search: term || undefined, cursors } });
      setItems((p) => [...p, ...(r?.items ?? [])]); setCursors(r?.cursors ?? null); setHasMore(!!r?.hasMore);
    } catch (e) { console.error(e); setError('Could not load more.'); } finally { setLoadingMore(false); }
  }

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-secondary sm:text-3xl"><Clock className="h-6 w-6 text-primary" />To Be Reviewed</h1>
          <p className="mt-1 text-sm text-muted-foreground">Open a decision to review, edit, and publish it by uploading the final PDF.</p>
        </div>
        <span className="rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">{pending} pending</span>
      </div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex gap-1 rounded-full bg-[hsl(var(--track))] p-1">
          {(['all', ...(user?.libraries ?? [])] as Lib[]).map((l) => (
            <button key={l} onClick={() => setLib(l)} className={`rounded-full px-4 py-1.5 text-sm font-medium ${lib === l ? 'bg-card text-secondary shadow-sm' : 'text-muted-foreground'}`}>{l === 'all' ? 'All' : l === 'itp' ? 'ITP' : 'Sport Dispute'}</button>
          ))}
        </div>
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search case reference or summary…" className="glass-input h-10 w-full rounded-xl pl-9 text-sm outline-none" />
          {refetching && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-primary" />}
        </div>
      </div>
      {error && <div className="mb-4 rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
      <div className={`overflow-x-auto rounded-xl bg-card shadow-sm ring-1 ring-border ${refetching ? 'opacity-60' : ''}`}>
        <table className="w-full min-w-[860px] text-sm">
          <thead className="bg-muted text-left text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr>{['Case Ref', 'Library', 'Group', 'Document', 'Sport', 'Adjudicator', 'Date', 'Finding', 'Fields', ''].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody>
            {loading ? Array.from({ length: 6 }).map((_, i) => <tr key={i}><td colSpan={10} className="px-4 py-3"><div className="h-5 animate-pulse rounded bg-muted" /></td></tr>)
              : items.length === 0 ? <tr><td colSpan={10} className="py-16 text-center text-muted-foreground"><Clock className="mx-auto mb-2 h-8 w-8 opacity-30" />Nothing waiting for review</td></tr>
              : items.map((d) => {
                const c = completeness(d);
                return (
                  <tr key={d.key} onClick={() => setOpen(d)} className="cursor-pointer border-t border-border hover:bg-muted/50">
                    <td className="px-4 py-3 font-medium text-secondary">{d.name}</td>
                    <td className="px-4 py-3">{LIB_LABEL[d.lib]}</td>
                    <td className="px-4 py-3 text-muted-foreground">{d.group}</td>
                    <td className="px-4 py-3">{d.document || '—'}</td>
                    <td className="px-4 py-3">{d.sport || '—'}</td>
                    <td className="px-4 py-3">{d.adjudicator || '—'}</td>
                    <td className="px-4 py-3 tabular-nums">{fmtDate(d.date)}</td>
                    <td className="px-4 py-3">{d.finding[0] ? <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">{d.finding[0]}</span> : '—'}</td>
                    <td className="px-4 py-3 tabular-nums"><span className={`mr-1.5 inline-block h-2 w-2 rounded-full ${c === 5 ? 'bg-[hsl(var(--success))]' : 'bg-primary/50'}`} />{c}/5</td>
                    <td className="px-4 py-3">{isStaff && <span className="flex items-center gap-1 whitespace-nowrap rounded-lg border border-primary/20 bg-primary/5 px-2.5 py-1 text-xs font-medium text-primary"><FileEdit className="h-3.5 w-3.5" />Open for Review</span>}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      {hasMore && <div className="mt-4 flex justify-center"><button onClick={more} disabled={loadingMore} className="flex h-10 items-center gap-2 rounded-xl border border-border bg-card px-5 text-sm font-medium disabled:opacity-60">{loadingMore && <Loader2 className="h-4 w-4 animate-spin" />}Load more</button></div>}
      {open && isStaff && <ReviewDecisionModal decision={open} onClose={() => setOpen(null)} onSaved={(u) => setItems((p) => p.map((x) => (x.key === u.key ? u : x)))} />}
    </div>
  );
}
